import React, { useState } from "react";
import { INotificationData } from "../MobileAppTypes";
import { NotificationController } from "./NotificationController";
import NotificationItem from "./NotificationItem";
import Button from "../../_Core/Button";

interface INotificationListProps {
  notifications: INotificationData[];
  onRefresh: () => void;
  isLoading?: boolean;
  requestNew?: () => void;
}

const NotificationList: React.FC<INotificationListProps> = ({
  notifications,
  onRefresh,  
  requestNew,
  isLoading = false,
}) => {
  const [cancelingIds, setCancelingIds] = useState<Set<string>>(new Set());

  const handleCancelNotification = async (notificationId: string) => {
    setCancelingIds(prev => new Set(prev).add(notificationId));
    
    try {
      const response = await NotificationController.cancelNotification(notificationId);
      
      if (response.Success) {
        // Refresh the list to show updated status
        onRefresh();
      }
    } catch (error) {
      console.error('Error canceling notification:', error);
    } finally {
      setCancelingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notificationId);
        return newSet;
      });
    }
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    // Sort by creation date, newest first
    const dateA = a.CreatedAtUtc ? new Date(a.CreatedAtUtc).getTime() : 0;
    const dateB = b.CreatedAtUtc ? new Date(b.CreatedAtUtc).getTime() : 0;
    return dateB - dateA;
  });

  const getNotificationCounts = () => {
    const counts = {
      total: notifications.length,
      sent: notifications.filter(n => n.Sent).length,
      scheduled: notifications.filter(n => n.Scheduled && !n.Sent).length,
      draft: notifications.filter(n => !n.Scheduled && !n.Sent).length
    };
    return counts;
  };

  const counts = getNotificationCounts();

  if (isLoading) {
    return (
      <div className="notification-list">
        <div className="notification-list__header">
          <h3 className="notification-list__title">Notifications</h3>
          <button 
            className="notification-list__refresh-btn"
            disabled
          >
            <span className="loading-spinner"></span>
            Loading...
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <span className="loading-spinner"></span>
          <div style={{ marginTop: '16px' }}>Loading notifications...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex-between">
        <h3>
          Notifications ({counts.total})
        </h3>
        <div className="btn-group">
        <Button 
        type="refresh"
          onClick={onRefresh}
          disabled={isLoading}
          text="Refresh"
        />
        <Button 
        type="new"
          onClick={requestNew}
          text="New"
        />
        </div>
      </div>


      {counts.total > 0 && (
        <div className="flex-between">
          <span>📨 Sent: {counts.sent}</span>
          <span>⏰ Scheduled: {counts.scheduled}</span>
          <span>📝 Draft: {counts.draft}</span>
        </div>
      )}
      <hr/>

      {sortedNotifications.length === 0 ? (
        <div className="notification-list__empty">
          <div className="notification-list__empty-icon">📱</div>
          <div className="notification-list__empty-text">
            No notifications found. Create your first notification to get started!
          </div>
        </div>
      ) : (
        <div className="item-list">
          {sortedNotifications.map((notification) => (
            <NotificationItem
              key={notification.PublicId || `notification-${Math.random()}`}
              notification={notification}
              onCancel={handleCancelNotification}
              isLoading={notification.PublicId ? cancelingIds.has(notification.PublicId) : false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationList;